import { useState, useEffect, useRef } from 'react';
import { Loader2, Save, AlertCircle, Sparkles, CheckCircle2, Search, X, Plus, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const PARSER_TYPES = [
  { value: 'rss',               label: 'RSS',                help: 'Standard RSS XML feed.' },
  { value: 'atom',              label: 'Atom',               help: 'Atom XML feed.' },
  { value: 'jsonld_news',       label: 'JSON-LD',            help: 'schema.org NewsArticle structured data embedded in the page.' },
  { value: 'sitemap_news',      label: 'News Sitemap',       help: 'Google News sitemap with the news: namespace.' },
  { value: 'newsroom_listing',  label: 'Newsroom Listing',   help: 'HTML listing scrape with CSS selectors.' },
  { value: 'custom',            label: 'Custom',             help: 'Not yet implemented.', disabled: true },

];

const FETCH_FREQUENCIES = [
  { value: 60,   label: 'Every 1 hour'   },
  { value: 360,  label: 'Every 6 hours'  },
  { value: 720,  label: 'Every 12 hours' },
  { value: 1440, label: 'Every 24 hours' },
];

const PRIORITY_BANDS = [
  { value: 'low',    label: 'Low',    priority: 1 },
  { value: 'medium', label: 'Medium', priority: 5 },
  { value: 'high',   label: 'High',   priority: 10 },
];

function priorityToBand(n) {
  const p = Number(n) || 0;
  if (p >= 8) return 'high';
  if (p >= 3) return 'medium';
  return 'low';
}

function bandToPriority(band) {
  return (PRIORITY_BANDS.find(b => b.value === band) || PRIORITY_BANDS[0]).priority;
}

const EMPTY = {
  source_name: '',
  source_url: '',
  parser_type: 'rss',
  parser_config_json: {},
  primary_organisation_id: '',
  primary_organisation_name: '',
  primary_person_id: '',
  is_active: true,
  fetch_frequency_minutes: 360,
  priority: 1,
  crawl_mode: 'standard',
  content_licence: '',
  region: '',
  notes: '',
};

const CONFIDENCE_BADGE = {
  high:   'bg-green-50 text-green-700 border-green-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low:    'bg-red-50 text-red-700 border-red-200',
};

function OrgCombobox({ value, onSelect, onClear }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const wrapperRef            = useRef(null);
  const cache                 = useRef({});

  // Inline create state
  const [creating, setCreating]   = useState(false);  
  const [saving, setSaving]       = useState(false);  
  const [newName, setNewName]     = useState('');
  const [newAliases, setNewAliases] = useState('');

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setLoading(false); return; }
    const key = query.toLowerCase();
    if (cache.current[key]) { setResults(cache.current[key]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/organisations', { params: { search: query } });
        const d = data || [];
        cache.current[key] = d;
        setResults(d);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const openCreate = (name) => {
    setNewName(name);
    setNewAliases('');
    setCreating(true);
    setOpen(false);
  };

  const submitCreate = async () => {
    const name = newName.trim();
    if (!name) return toast.error('Organisation name is required');
    setSaving(true);
    try {
      const aliases = newAliases.split(',').map(a => a.trim()).filter(Boolean);
      const { data } = await api.post('/organisations', { name, aliases });
      onSelect({ id: data.id, name: data.name });
      toast.success(`Organisation "${data.name}" created`);
      setCreating(false);
      setQuery('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create organisation');
    } finally {
      setSaving(false);
    }
  };

  if (value?.id) {
    return (
      <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
        <span className="text-sm font-medium text-slate-900">{value.name}</span>
        <button type="button" onClick={onClear}
          className="text-slate-400 hover:text-red-500">
          <X size={14} />
        </button>
      </div>
    );
  }

  if (creating) {
    return (
      <div className="space-y-3 p-3 rounded-lg border border-slate-200 bg-slate-50/60">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Create organisation</p>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Name <span className="text-red-500">*</span></label>
          <input value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Full organisation name"
            className={inputClass} />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">
            Aliases <span className="text-slate-400">(comma separated)</span>
          </label>
          <input value={newAliases}
            onChange={e => setNewAliases(e.target.value)}
            placeholder="e.g. Brent Council, LB Brent"
            className={inputClass} />
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={submitCreate} disabled={saving || !newName.trim()}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            {saving ? 'Creating...' : 'Create'}
          </button>
          <button type="button" onClick={() => setCreating(false)} disabled={saving}
            className="px-3 py-1.5 text-slate-400 hover:text-slate-700 text-xs transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const queryLong = query.trim().length >= 2;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search or create organisation..."
          className={`${inputClass} pl-8 pr-8`} />
        {loading && <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      </div>

      {open && queryLong && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto border border-slate-200 rounded-lg bg-white shadow-md">
          {results.map(o => (
            <button key={o.id} type="button"
              onClick={() => { onSelect(o); setQuery(''); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-900 hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
              {o.name}
              {o.aliases?.length > 0 && (
                <span className="text-xs text-slate-400 ml-1.5">aka {o.aliases.slice(0, 2).join(', ')}</span>
              )}
            </button>
          ))}
          <button type="button"
            onClick={() => openCreate(query.trim())}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 inline-flex items-center gap-1.5">
            <Plus size={13} /> {results.length > 0 ? `Create "${query.trim()}"` : `No match — create "${query.trim()}"`}
          </button>
        </div>
      )}
    </div>
  );
}


export default function SourceSettingsTab({ source, onSaved, onCancel }) {
  const isNew = !source?.id;

  const [form, setForm]   = useState(
    source
      ? { ...EMPTY, ...source, parser_config_json: source.parser_config_json || {} }
      : { ...EMPTY, parser_config_json: {} }
  );
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [detectResult, setDetectResult] = useState(null);
  const [urlUnlocked, setUrlUnlocked] = useState(false);
  const [showTried, setShowTried] = useState(false);
  const urlEditable = isNew || urlUnlocked;

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const updateConfig = (k, v) => setForm(prev => ({
    ...prev,
    parser_config_json: { ...(prev.parser_config_json || {}), [k]: v },
  }));

  const updatePagination = (k, v) => setForm(prev => ({
    ...prev,
    parser_config_json: {
      ...(prev.parser_config_json || {}),
      pagination: { ...((prev.parser_config_json || {}).pagination || {}), [k]: v },
    },
  }));
  const applyDoor = (door) => {
    if (!door) return;
    setForm(prev => {
       const referer = (prev.parser_config_json || {}).referer;
      const next = { ...(door.config || {}) };
      if (referer) next.referer = referer;
      return {
        ...prev,
        parser_type: door.parser_type,
        source_url: urlEditable && door.url ? door.url : prev.source_url,
        parser_config_json: next,
      };
    });
  };

  const handleDetect = async () => {
    if (!form.source_url?.trim()) return toast.error('Enter a URL first');
    setDetecting(true);
    setDetectResult(null);
    setShowTried(false);
    try {
      const { data } = await api.post('/feeds/sources/detect', { url: form.source_url.trim() });
      setDetectResult(data);
      if (data.recommended) {
        applyDoor(data.recommended);
        toast.success(`Detected: ${data.recommended.label}`);
      } else {
        toast.error('No feed found for that URL');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Detection failed');
    } finally {
      setDetecting(false);
    }
  };

  const buildConfig = () => {
    const c = form.parser_config_json || {};
    const toInt = v => {
      if (v === '' || v === null || v === undefined) return undefined;
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? undefined : n;
    };
    const clean = obj => {
      const out = {};
      Object.entries(obj).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        out[k] = typeof v === 'string' ? v.trim() : v;
      });
      return out;
    };

    const referer = (c.referer || '').trim();

    if (form.parser_type === 'newsroom_listing') {
      const cfg = clean({
        item_selector:    c.item_selector,
        link_selector:    c.link_selector,
        title_selector:   c.title_selector,
        date_selector:    c.date_selector,
        date_format:      c.date_format,
        summary_selector: c.summary_selector,
        image_selector:   c.image_selector,
        base_url:         c.base_url,
        referer,
      });
      const pag = clean({
        param:         c.pagination?.param,
        next_selector: c.pagination?.next_selector,
        max_pages:     toInt(c.pagination?.max_pages),
      });
      if (Object.keys(pag).length) cfg.pagination = pag;
      return Object.keys(cfg).length ? cfg : null;
    }

    if (form.parser_type === 'sitemap_news') {
      const cfg = clean({
        sitemap_url:        c.sitemap_url,
        since_days:         toInt(c.since_days),
        max_urls:           toInt(c.max_urls),
        max_child_sitemaps: toInt(c.max_child_sitemaps),
        url_include:        c.url_include,
        url_exclude:        c.url_exclude,
        gc_api_host:        c.gc_api_host,
        referer,
      });
      return Object.keys(cfg).length ? cfg : null;
    }

    if (form.parser_type === 'jsonld_news') {
      const cfg = clean({
        since_days: toInt(c.since_days),
        max_items:  toInt(c.max_items),
        referer,
      });
      return Object.keys(cfg).length ? cfg : null;
    }

    const base = clean({ referer });
    return Object.keys(base).length ? base : null;
  };

  const handleSubmit = async () => {
    if (!form.source_name?.trim())              return toast.error('Name is required');
    if (!form.source_url?.trim())               return toast.error('URL is required');
    if (!form.primary_organisation_id)          return toast.error('Primary organisation is required');
    if (form.parser_type === 'newsroom_listing' && !form.parser_config_json?.item_selector?.trim())
      return toast.error('Item selector is required for newsroom listing');

    setSaving(true);
    try {
      const payload = {
        source_name: form.source_name.trim(),
        source_url:  form.source_url.trim(),
        parser_type: form.parser_type,
        parser_config_json: buildConfig(),
        primary_organisation_id: form.primary_organisation_id,
        primary_person_id: form.primary_person_id || null,
        is_active: form.is_active,
        fetch_frequency_minutes: parseInt(form.fetch_frequency_minutes, 10),
        priority: bandToPriority(priorityToBand(form.priority)),
        crawl_mode: form.crawl_mode,
        content_licence: form.content_licence || null,
        region: form.region || null,
        notes: form.notes || null,
      };

      let result;
      if (isNew) {
        const { data } = await api.post('/feeds/sources', payload);
        result = data;
        toast.success('Source created');
      } else {
        const { data } = await api.put(`/feeds/sources/${source.id}`, payload);
        result = data;
      }
      onSaved?.(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const config = form.parser_config_json || {};
  const rec = detectResult?.recommended || null;
  const tried = detectResult?.tried || [];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="space-y-5 max-w-2xl">
        <Field label="Source name" required>
          <input value={form.source_name}
            onChange={e => update('source_name', e.target.value)}
            placeholder="e.g. Bank of England — News"
            className={inputClass} />
        </Field>

        <Field label="Source URL" required>
          <input value={form.source_url}
            onChange={e => update('source_url', e.target.value)}
            placeholder="https://www.bankofengland.co.uk/rss/news"
            disabled={!urlEditable}
            className={`${inputClass} font-mono text-xs ${!urlEditable ? 'opacity-60 cursor-not-allowed' : ''}`} />
          {!isNew && !urlUnlocked && (
            <div className="flex items-center justify-between gap-2 mt-1">
              <p className="text-xs text-slate-400 flex items-center gap-1">
                <AlertCircle size={11} /> URL is locked to preserve item dedup history.
              </p>
              <button type="button" onClick={() => setUrlUnlocked(true)}
                className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 shrink-0">
                <Unlock size={11} /> Change URL
              </button>
            </div>
          )}
          {!isNew && urlUnlocked && (
            <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
              <AlertCircle size={11} className="mt-0.5 shrink-0" />
              Changing the URL repoints this source at a different door on the same site.
              Existing items stay, but they will not dedup against the new URLs, so the
              first fetch may re-ingest recent articles.
            </p>
          )}
        </Field>

        {urlEditable && (
          <div className="space-y-2 -mt-2">
            <button type="button" onClick={handleDetect}
              disabled={detecting || !form.source_url?.trim()}
              className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white px-3.5 py-2 rounded-lg text-sm font-medium transition-colors">
              {detecting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {detecting ? 'Detecting...' : 'Auto-detect feed type'}
            </button>
            <p className="text-xs text-slate-400">
              Probes the URL for the best feed method — RSS, then structured data, news sitemap, then page scrape — and fills the form. You can override anything below.
            </p>

            {detectResult && rec && (
              <div className="p-3 rounded-lg border border-slate-200 bg-slate-50/60 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <CheckCircle2 size={14} className="text-green-600" />
                  <span className="text-sm text-slate-900 font-medium">{rec.label}</span>
                  <span className={`text-[11px] px-1.5 py-0.5 rounded border ${CONFIDENCE_BADGE[rec.confidence] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                    {rec.confidence} confidence
                  </span>
                  <span className="text-xs text-slate-400">{rec.items} items found</span>
                </div>

                {(rec.warnings || []).map((w, i) => (
                  <p key={i} className="text-xs text-amber-700 flex items-start gap-1">
                    <AlertCircle size={11} className="mt-0.5 shrink-0" /> {w}
                  </p>
                ))}

                {detectResult.doors.length > 1 && (
                  <div className="flex items-center gap-1.5 flex-wrap pt-1">
                    <span className="text-xs text-slate-400">Other methods:</span>
                    {detectResult.doors.map(d => (
                      <button key={d.parser_type} type="button" onClick={() => applyDoor(d)}
                        className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                          form.parser_type === d.parser_type
                            ? 'border-blue-400 bg-blue-50 text-blue-700'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}>
                        {d.label} ({d.items})
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {detectResult && !rec && (
              <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-700 space-y-1.5">
                <div className="flex items-start gap-1.5">
                  <AlertCircle size={13} className="mt-0.5 shrink-0" />
                  <span>
                    {detectResult.landing_blocked
                      ? 'The page itself could not be fetched — the site is blocking the request rather than lacking a feed. Try a Referer override below, or point the URL at a sitemap or feed path directly.'
                      : 'No machine-readable feed found'}
                    {!detectResult.landing_blocked && detectResult.js_rendered
                      ? ' — the page looks JS-rendered, so HTML scraping may not work either.'
                      : detectResult.landing_blocked ? '' : '.'}
                    {' '}Set the parser type manually below.
                  </span>
                </div>
              </div>
            )}

            {detectResult && tried.length > 0 && (
              <div className="text-xs">
                <button type="button" onClick={() => setShowTried(v => !v)}
                  className="text-slate-400 hover:text-slate-600 transition-colors">
                  {showTried ? 'Hide' : 'Show'} what was probed ({tried.length})
                </button>
                {showTried && (
                  <div className="mt-1.5 max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {tried.map((t, i) => (
                      <div key={i} className="px-2.5 py-1.5">
                        <p className="font-mono text-[11px] text-slate-600 break-all">{t.url}</p>
                        <p className="text-[11px] text-slate-400">{t.outcome}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <Field label="Parser type" required>
          <div className="space-y-1.5">
            {PARSER_TYPES.map(p => (
              <label key={p.value} className={`flex items-start gap-2 p-2.5 rounded border ${
                form.parser_type === p.value
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
              } ${p.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                <input type="radio" name="parser_type" value={p.value}
                  checked={form.parser_type === p.value}
                  disabled={p.disabled}
                  onChange={() => update('parser_type', p.value)}
                  className="mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-slate-900 font-medium">{p.label}</p>
                  <p className="text-xs text-slate-400">{p.help}</p>
                </div>
              </label>
            ))}
          </div>
        </Field>

        <Field
          label="Referer override"
          help="Optional. Sent as the Referer header on every fetch. Defaults to the source's own domain — only set this when a site checks for a specific referring domain (e.g. norfolk.gov.uk for a police subdomain)."
        >
          <input value={config.referer || ''}
            onChange={e => updateConfig('referer', e.target.value)}
            placeholder="https://www.norfolk.gov.uk/"
            className={`${inputClass} font-mono text-xs`} />
        </Field>

        {form.parser_type === 'jsonld_news' && (
          <div className="space-y-4 p-4 rounded-lg border border-slate-200 bg-slate-50/60">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Structured data options</p>
              {rec?.parser_type === 'jsonld_news' && (
                <span className={`text-[11px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${CONFIDENCE_BADGE[rec.confidence] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                  <Sparkles size={10} /> Auto-detected · {rec.confidence}
                </span>
              )}
            </div>

            <p className="text-xs text-slate-400">
              No selectors needed. Reads schema.org NewsArticle data straight from the page. Both fields optional.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Since days" help="Only entries within N days.">
                <input type="number" min="1" value={config.since_days || ''}
                  onChange={e => updateConfig('since_days', e.target.value)}
                  placeholder="31"
                  className={inputClass} />
              </Field>

              <Field label="Max items" help="Default 100.">
                <input type="number" min="1" value={config.max_items || ''}
                  onChange={e => updateConfig('max_items', e.target.value)}
                  placeholder="100"
                  className={inputClass} />
              </Field>
            </div>
          </div>
        )}

        {form.parser_type === 'newsroom_listing' && (
          <div className="space-y-4 p-4 rounded-lg border border-slate-200 bg-slate-50/60">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Listing selectors</p>
              {rec?.parser_type === 'newsroom_listing' && (
                <span className={`text-[11px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${CONFIDENCE_BADGE[rec.confidence] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                  <Sparkles size={10} /> Auto-detected · {rec.confidence}
                </span>
              )}
            </div>

            {rec?.parser_type === 'newsroom_listing' && (rec.warnings || []).map((w, i) => (
              <p key={i} className="text-xs text-amber-700 flex items-start gap-1">
                <AlertCircle size={11} className="mt-0.5 shrink-0" /> {w}
              </p>
            ))}

            <Field label="Item selector" required help="CSS for the repeating row. e.g. ul.news-list li">
              <input value={config.item_selector || ''}
                onChange={e => updateConfig('item_selector', e.target.value)}
                placeholder="ul.news-list li"
                className={`${inputClass} font-mono text-xs`} />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Title selector" help="Defaults to link text.">
                <input value={config.title_selector || ''}
                  onChange={e => updateConfig('title_selector', e.target.value)}
                  placeholder="a"
                  className={`${inputClass} font-mono text-xs`} />
              </Field>

              <Field label="Link selector" help="Defaults to first <a> in the row.">
                <input value={config.link_selector || ''}
                  onChange={e => updateConfig('link_selector', e.target.value)}
                  placeholder="a"
                  className={`${inputClass} font-mono text-xs`} />
              </Field>

              <Field label="Date selector">
                <input value={config.date_selector || ''}
                  onChange={e => updateConfig('date_selector', e.target.value)}
                  placeholder="time, .date"
                  className={`${inputClass} font-mono text-xs`} />
              </Field>

              <Field label="Date format" help="Optional strptime. e.g. %d %B %Y">
                <input value={config.date_format || ''}
                  onChange={e => updateConfig('date_format', e.target.value)}
                  placeholder="%d %B %Y"
                  className={`${inputClass} font-mono text-xs`} />
              </Field>

              <Field label="Summary selector">
                <input value={config.summary_selector || ''}
                  onChange={e => updateConfig('summary_selector', e.target.value)}
                  placeholder=".summary"
                  className={`${inputClass} font-mono text-xs`} />
              </Field>

              <Field label="Image selector">
                <input value={config.image_selector || ''}
                  onChange={e => updateConfig('image_selector', e.target.value)}
                  placeholder="img"
                  className={`${inputClass} font-mono text-xs`} />
              </Field>
            </div>

            <Field label="Base URL" help="Optional. Resolves relative links. Defaults to the source URL.">
              <input value={config.base_url || ''}
                onChange={e => updateConfig('base_url', e.target.value)}
                placeholder="https://www.example.gov.uk"
                className={`${inputClass} font-mono text-xs`} />
            </Field>

            <div className="grid grid-cols-3 gap-4">
              <Field label="Max pages" help="Default 1.">
                <input type="number" min="1" value={config.pagination?.max_pages || ''}
                  onChange={e => updatePagination('max_pages', e.target.value)}
                  placeholder="1"
                  className={inputClass} />
              </Field>

              <Field label="Page param" help="?param=N style.">
                <input value={config.pagination?.param || ''}
                  onChange={e => updatePagination('param', e.target.value)}
                  placeholder="page"
                  className={`${inputClass} font-mono text-xs`} />
              </Field>

              <Field label="Next selector" help="Or a 'next page' link.">
                <input value={config.pagination?.next_selector || ''}
                  onChange={e => updatePagination('next_selector', e.target.value)}
                  placeholder="a.next"
                  className={`${inputClass} font-mono text-xs`} />
              </Field>
            </div>
          </div>
        )}

        {form.parser_type === 'sitemap_news' && (
          <div className="space-y-4 p-4 rounded-lg border border-slate-200 bg-slate-50/60">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Sitemap options</p>
              {rec?.parser_type === 'sitemap_news' && (
                <span className={`text-[11px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${CONFIDENCE_BADGE[rec.confidence] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                  <Sparkles size={10} /> Auto-detected · {rec.confidence}
                </span>
              )}
            </div>

            <Field label="Sitemap URL" help="Optional. Override if the source URL is a landing page, not the sitemap itself.">
              <input value={config.sitemap_url || ''}
                onChange={e => updateConfig('sitemap_url', e.target.value)}
                placeholder="https://www.example.gov.uk/news-sitemap.xml"
                className={`${inputClass} font-mono text-xs`} />
            </Field>

            <div className="grid grid-cols-3 gap-4">
              <Field label="Since days" help="Only entries within N days.">
                <input type="number" min="1" value={config.since_days || ''}
                  onChange={e => updateConfig('since_days', e.target.value)}
                  placeholder="30"
                  className={inputClass} />
              </Field>

              <Field label="Max URLs" help="Default 200.">
                <input type="number" min="1" value={config.max_urls || ''}
                  onChange={e => updateConfig('max_urls', e.target.value)}
                  placeholder="200"
                  className={inputClass} />
              </Field>

              <Field label="Max child sitemaps" help="For index sitemaps. Default 5.">
                <input type="number" min="1" value={config.max_child_sitemaps || ''}
                  onChange={e => updateConfig('max_child_sitemaps', e.target.value)}
                  placeholder="5"
                  className={inputClass} />
              </Field>
            </div>

            <Field label="URL include pattern" help="Regex matched against each URL. Keep only those that match — e.g. dated news article paths. Required for a flat site sitemap, or you will ingest ordinary pages as articles.">
              <input value={config.url_include || ''}
                onChange={e => updateConfig('url_include', e.target.value)}
                placeholder="/news/"
                className={`${inputClass} font-mono text-xs`} />
            </Field>

            <Field label="URL exclude pattern" help="Optional regex. Drop URLs that match — e.g. category, video or fixture pages.">
              <input value={config.url_exclude || ''}
                onChange={e => updateConfig('url_exclude', e.target.value)}
                placeholder="/videos"
                className={`${inputClass} font-mono text-xs`} />
            </Field>

            <Field label="GC article API host" help="Optional. GameChanger club sites: fetch article bodies from this host's byslug API instead of scraping the page.">
              <input value={config.gc_api_host || ''}
                onChange={e => updateConfig('gc_api_host', e.target.value)}
                placeholder="news.cms.web.gc.example.co.uk"
                className={`${inputClass} font-mono text-xs`} />
            </Field>
          </div>
        )}

        <Field label="Primary organisation" required help="Every source must be tagged to an org. Entity-first principle.">
          <OrgCombobox
            value={form.primary_organisation_id
              ? { id: form.primary_organisation_id, name: form.primary_organisation_name }
              : null}
            onSelect={o => setForm(prev => ({
              ...prev,
              primary_organisation_id: o.id,
              primary_organisation_name: o.name,
            }))}
            onClear={() => setForm(prev => ({
              ...prev,
              primary_organisation_id: '',
              primary_organisation_name: '',
            }))}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Fetch frequency">
            <select value={form.fetch_frequency_minutes}
              onChange={e => update('fetch_frequency_minutes', parseInt(e.target.value, 10))}
              className={inputClass}>
              {FETCH_FREQUENCIES.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Priority" help="Higher priority is fetched first.">
            <select value={priorityToBand(form.priority)}
              onChange={e => update('priority', bandToPriority(e.target.value))}
              className={inputClass}>
              {PRIORITY_BANDS.map(b => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Content licence" help="Optional. e.g. OGL v3.0">
            <input value={form.content_licence || ''}
              onChange={e => update('content_licence', e.target.value)}
              className={inputClass} />
          </Field>

          <Field label="Region" help="Optional. e.g. UK, England">
            <input value={form.region || ''}
              onChange={e => update('region', e.target.value)}
              className={inputClass} />
          </Field>
        </div>

        <Field label="Notes">
          <textarea value={form.notes || ''}
            onChange={e => update('notes', e.target.value)}
            rows={2}
            className={inputClass} />
        </Field>

        {!isNew && (
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pt-2 border-t border-slate-200">
            <input type="checkbox" checked={form.is_active}
              onChange={e => update('is_active', e.target.checked)} />
            Source is active (uncheck to pause scheduled fetches)
          </label>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 mt-6 pt-5 border-t border-slate-200">
        {onCancel && (
          <button onClick={onCancel}
            className="px-4 py-2 text-slate-400 hover:text-slate-700 text-sm transition-colors">
            Cancel
          </button>
        )}
        <button onClick={handleSubmit} disabled={saving}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {isNew ? 'Create Source' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}


const inputClass = "w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500";


function Field({ label, required, help, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {help && <p className="text-xs text-slate-400 mt-1">{help}</p>}
    </div>
  );
}